import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Lock, PlayCircle, ShieldCheck, Award } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "@/lib/api";
import { useCourse, useProgress, currentTierData, TIER_COPY, credentialNote } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";

const QuizActions = ({ moduleId, state, tier }) => {
  const navigate = useNavigate();
  const start = async (mode) => {
    try {
      const { data } = await api.post("/quiz/start", { module_id: moduleId, mode });
      navigate(`/quiz/${data.attempt_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const canCertify = state.unlocked && state.read_complete && !state.passed && tier.status !== "certified";
  return (
    <div className="border border-border bg-card p-6">
      <div className="overline-label">Module quiz · {TIER_COPY[tier.tier].label}</div>
      <p className="mt-2 text-sm text-muted-foreground">
        Certified check: 10 adaptive questions ({tier.blueprint.easy} easy · {tier.blueprint.medium} medium · {tier.blueprint.hard} hard). Pass at 8/10 or an 80% supported score with hints. Practice: 5 questions with instant feedback.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button data-testid="start-practice-quiz-button" onClick={() => start("practice")} className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
          <PlayCircle className="h-4 w-4" /> Practice quiz
        </button>
        <button data-testid="start-certified-quiz-button" disabled={!canCertify} onClick={() => start("certified")} className="inline-flex items-center gap-2 rounded-sm bg-brand-action px-4 py-2 text-sm font-semibold text-brand-action-fg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45">
          <ShieldCheck className="h-4 w-4" /> Certified quiz
        </button>
      </div>
      {state.passed && <p data-testid="module-passed-note" className="mt-3 text-sm text-brand-accent">Passed in this tier with {state.certified_score}%.</p>}
      {!state.read_complete && !state.passed && <p data-testid="module-read-required-note" className="mt-3 text-xs text-muted-foreground">Read all lessons and complete the required plant decision to unlock the certified check.</p>}
    </div>
  );
};

export default function ModuleView() {
  const { moduleId } = useParams();
  const { data: course } = useCourse();
  const { data: progress } = useProgress();
  if (!course || !progress) return <Loader />;
  const m = course.modules.find((x) => x.id === moduleId);
  const tier = currentTierData(progress);
  const state = tier.modules.find((s) => s.module_id === moduleId);
  if (!m) return <PageShell>Module not found.</PageShell>;
  return (
    <PageShell>
      <Link to="/dashboard" data-testid="module-back-link" className="text-sm text-muted-foreground hover:text-foreground">← My path</Link>
      <div className="mt-6 overline-label">Day {m.day} · {m.theme} · {m.id}</div>
      <h1 data-testid="module-title" className="mt-2 font-heading text-3xl font-black tracking-tight sm:text-4xl">{m.title}</h1>
      {m.studio && <div data-testid="module-studio" className="mt-2 font-heading text-lg font-bold text-brand-primary">{m.studio}</div>}
      {!state.unlocked && (
        <p data-testid="module-locked-note" className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Lock className="h-4 w-4" /> {state.open ? "Admin preview: lessons and labs are open. Quizzes unlock after the previous module's certified quiz." : "Pass the previous module's certified quiz to unlock."}</p>
      )}
      <p className="mt-5 max-w-3xl text-muted-foreground">{m.purpose}</p>
      {m.badge && <p data-testid="module-badge" className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold ${state.passed ? "text-brand-accent" : "text-muted-foreground"}`}><Award className="h-4 w-4" />{state.passed ? "Badge earned" : "Badge for passing this module"}: {m.badge}</p>}
      {m.case_clue && <p data-testid="module-case-clue" className="mt-3 max-w-3xl rounded-r-lg border-l-4 border-brand-data bg-brand-data/5 p-3 text-sm"><span className="font-semibold">Line 3 case clue: </span>{m.case_clue}</p>}
      {m.id === "M08" && <p data-testid="module-credential-note" className="mt-3 max-w-3xl text-sm text-muted-foreground">{credentialNote()}</p>}
      {m.orientation && <details className="mt-4 max-w-3xl border-l-4 border-brand-action bg-card p-4 text-sm"><summary className="cursor-pointer font-semibold">Why this matters</summary><div className="reader mt-3 text-base"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.orientation}</ReactMarkdown></div></details>}
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <ol className="lg:col-span-7">
          {m.lessons.map((l) => {
            const done = tier.lessons_done.includes(l.id);
            return (
              <li key={l.id}>
                <Link to={!state.open ? "#" : `/lessons/${l.id}`} data-testid={`lesson-link-${l.id}`} className={`flex items-center gap-4 border-b border-border py-4 transition-colors hover:bg-muted/50 ${!state.open ? "pointer-events-none opacity-50" : ""} ${l.kind === "lab" ? "bg-brand-data/5" : ""}`}>
                  <span className={`flex h-7 min-w-[28px] items-center justify-center border px-1 ${done ? "border-brand-accent bg-brand-accent text-brand-accent-fg" : l.kind === "lab" ? "border-brand-data" : "border-border"}`}>{done ? <Check className="h-4 w-4" /> : <span className="font-mono text-[11px]">{l.kind === "lab" ? l.number.replace("Lab ", "L") : l.number}</span>}</span>
                  <span className="flex-1 font-medium">{l.kind === "lab" && <span className="mr-2 font-mono text-[10px] uppercase text-brand-data">Try a plant decision · required</span>}{l.title}</span>
                  <span className="font-mono text-xs text-muted-foreground">{l.minutes} min</span>
                </Link>
              </li>
            );
          })}
        </ol>
        <div className="space-y-6 lg:col-span-5">
          <QuizActions moduleId={moduleId} state={state} tier={tier} />
          {m.activity && (
            <div className="border border-border bg-muted/40 p-6">
              <div className="overline-label">Module activity</div>
              <div className="reader mt-3 text-base"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.activity}</ReactMarkdown></div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
